import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserRoleName, isAdminUser, isPlatformOwner } from '../utils/authUser';
import { NotificationsProvider } from '../context/NotificationsContext';
import ChangePasswordModal from './ChangePasswordModal';
import NotificationPreferencesModal from './NotificationPreferencesModal';
import NotificationBell from './notifications/NotificationBell';

const Layout: React.FC = () => {
  const { logout, user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isNotificationPrefsOpen, setIsNotificationPrefsOpen] = useState(false);
  const location = useLocation();
  const [activeWineryName, setActiveWineryName] = useState<string>('');

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load active winery branding
  useEffect(() => {
    const name = localStorage.getItem('activeWineryName') || 'Winery';
    setActiveWineryName(name);
  }, []);

  // Platform owners use Sotimer backoffice, not the winery shell
  if (isPlatformOwner(user)) {
    return <Navigate to="/backoffice" replace />;
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/backoffice' || path === '/tools') {
      return location.pathname.startsWith(path);
    }
    return location.pathname === path;
  };

  const navItems = [
    { path: '/dashboard', name: 'Dashboard', icon: FiIcons.FiHome },
    { path: '/vineyards', name: 'Vineyards', icon: FiIcons.FiMapPin },
    { path: '/batches', name: 'Wine Batches', icon: FiIcons.FiDroplet },
    { path: '/fermentation', name: 'Fermentation', icon: FiIcons.FiActivity },
    { path: '/aging', name: 'Aging', icon: FiIcons.FiClock },
    { path: '/bottling', name: 'Bottling', icon: FiIcons.FiPackage },
    { path: '/inventory', name: 'Inventory', icon: FiIcons.FiArchive },
    { path: '/vessels', name: 'Vessels', icon: FiIcons.FiDatabase },
    { path: '/tools', name: 'Tools', icon: FiIcons.FiTool },
  ];

  const roleName = getUserRoleName(user);
  const isAdmin = isAdminUser(user);
  const showPlatformLink = isPlatformOwner(user);
  const footerLinkClass =
    'group flex items-center w-full px-3 py-2 text-[13px] font-medium rounded-xl text-monday-sidebar-text hover:bg-white/[0.07] hover:text-white transition-all duration-200';
  const mobileFooterLinkClass =
    'group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-xl text-monday-sidebar-text hover:bg-white/[0.07] hover:text-white transition-all duration-200';

  // Get user initials
  const userInitials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.toUpperCase() || 'U';

  return (
    <NotificationsProvider>
    <div className="h-screen flex overflow-hidden bg-monday-bg text-monday-text">
      {/* ── Mobile Sidebar Overlay ──────────────────────────────────── */}
      {isMobile && (
        <div className={`fixed inset-0 z-40 flex ${isSidebarOpen ? '' : 'pointer-events-none'}`}>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/50 transition-opacity duration-200 ${
              isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={closeSidebar}
          />

          {/* Sidebar Panel */}
          <div
            className={`relative flex-1 flex flex-col max-w-[300px] w-full bg-monday-sidebar bg-[radial-gradient(circle_at_top_left,rgba(180,35,58,0.28),transparent_18rem)] transition-transform duration-250 ease-out ${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {/* Close button */}
            <div className="absolute top-0 right-0 -mr-12 pt-3">
              <button
                className={`flex items-center justify-center h-10 w-10 rounded-full focus:outline-none ${
                  isSidebarOpen ? '' : 'pointer-events-none'
                }`}
                onClick={closeSidebar}
              >
                <span className="sr-only">Close sidebar</span>
                <div className="text-white">{FiIcons.FiX({ size: 22 })}</div>
              </button>
            </div>

            {/* Logo Area */}
            <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-white/10">
              <div aria-hidden className="shrink-0 mr-3 rounded-2xl bg-white/10 p-2 ring-1 ring-white/10">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 3h10v2a5 5 0 0 1-10 0V3Z" fill="#F4C874" fillOpacity="0.32" />
                  <path d="M12 10c3.314 0 6-2.239 6-5V3H6v2c0 2.761 2.686 5 6 5Z" stroke="#F4C874" strokeWidth="1.2" />
                  <path d="M12 10v7m0 0c-2.5 0-4 1-4 2h8c0-1-1.5-2-4-2Z" stroke="#F4C874" strokeWidth="1.2" />
                </svg>
              </div>
              <h1
                className="text-white font-semibold text-base leading-tight truncate tracking-tight"
                title={activeWineryName}
              >
                {activeWineryName}
              </h1>
            </div>

            {/* Mobile User Info */}
            <div className="flex items-center px-4 py-4 border-b border-white/10 bg-white/[0.03]">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-2xl bg-primary-500 flex items-center justify-center ring-2 ring-white/20 shadow-primary">
                  <span className="text-white text-xs font-semibold">
                    {userInitials}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-monday-sidebar-text truncate">
                  <span className="capitalize">{roleName || 'User'}</span>
                </p>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="mt-4 flex-1 overflow-y-auto sidebar-scroll">
              <nav className="px-3 space-y-1">
                {navItems.map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                        active
                          ? 'bg-white/10 text-white ring-1 ring-white/10 shadow-sm border-l-[3px] border-secondary-300 pl-[9px]'
                          : 'text-monday-sidebar-text hover:bg-white/[0.07] hover:text-white border-l-[3px] border-transparent pl-[9px]'
                      }`}
                      onClick={closeSidebar}
                    >
                      <div className="mr-3 flex-shrink-0">{Icon({ size: 18 })}</div>
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
                {showPlatformLink && (
                  <Link
                    to="/backoffice"
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 mt-4 border-t border-white/10 pt-4 ${
                      isActive('/backoffice')
                        ? 'bg-white/10 text-white ring-1 ring-white/10 shadow-sm border-l-[3px] border-secondary-300 pl-[9px]'
                        : 'text-monday-sidebar-text/70 hover:bg-white/[0.07] hover:text-white border-l-[3px] border-transparent pl-[9px]'
                    }`}
                    onClick={closeSidebar}
                  >
                    <div className="mr-3 flex-shrink-0">{FiIcons.FiLayers({ size: 18 })}</div>
                    <span>Platform</span>
                  </Link>
                )}
              </nav>
            </div>

            {/* Mobile user actions */}
            <div className="flex-shrink-0 border-t border-white/10 p-3 bg-black/10 space-y-1">
              {isAdmin && (
                <Link
                  to="/users"
                  onClick={closeSidebar}
                  className={mobileFooterLinkClass}
                >
                  <div className="mr-3">{FiIcons.FiUsers({ size: 18 })}</div>
                  Users
                </Link>
              )}
              <button
                type="button"
                onClick={() => {
                  setIsChangePasswordOpen(true);
                  closeSidebar();
                }}
                className={mobileFooterLinkClass}
              >
                <div className="mr-3">{FiIcons.FiLock({ size: 18 })}</div>
                Change password
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsNotificationPrefsOpen(true);
                  closeSidebar();
                }}
                className={mobileFooterLinkClass}
              >
                <div className="mr-3">{FiIcons.FiBell({ size: 18 })}</div>
                Email notifications
              </button>
              <button
                onClick={logout}
                className={mobileFooterLinkClass}
              >
                <div className="mr-3">{FiIcons.FiLogOut({ size: 18 })}</div>
                Logout
              </button>
            </div>
          </div>

          <div className="flex-shrink-0 w-14" aria-hidden="true" />
        </div>
      )}

      {/* ── Desktop Sidebar ─────────────────────────────────────────── */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-full bg-monday-sidebar bg-[radial-gradient(circle_at_top_left,rgba(180,35,58,0.24),transparent_18rem)]">
            {/* Logo Area */}
            <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-white/10">
              <div aria-hidden className="shrink-0 mr-3 rounded-2xl bg-white/10 p-2 ring-1 ring-white/10">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 3h10v2a5 5 0 0 1-10 0V3Z" fill="#F4C874" fillOpacity="0.32" />
                  <path d="M12 10c3.314 0 6-2.239 6-5V3H6v2c0 2.761 2.686 5 6 5Z" stroke="#F4C874" strokeWidth="1.2" />
                  <path d="M12 10v7m0 0c-2.5 0-4 1-4 2h8c0-1-1.5-2-4-2Z" stroke="#F4C874" strokeWidth="1.2" />
                </svg>
              </div>
              <h1
                className="text-white font-semibold text-base leading-tight truncate tracking-tight"
                title={activeWineryName}
              >
                {activeWineryName}
              </h1>
            </div>

            {/* Navigation */}
            <div className="flex-1 overflow-y-auto sidebar-scroll py-4">
              <nav className="px-3 space-y-1">
                {navItems.map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`group flex items-center px-3 py-2.5 text-[13px] font-medium rounded-xl transition-all duration-200 ${
                        active
                          ? 'bg-white/10 text-white ring-1 ring-white/10 shadow-sm border-l-[3px] border-secondary-300 pl-[9px]'
                          : 'text-monday-sidebar-text hover:bg-white/[0.07] hover:text-white border-l-[3px] border-transparent pl-[9px]'
                      }`}
                    >
                      <div className="mr-3 flex-shrink-0">{Icon({ size: 18 })}</div>
                      {item.name}
                    </Link>
                  );
                })}
                {showPlatformLink && (
                  <Link
                    to="/backoffice"
                    className={`group flex items-center px-3 py-2.5 text-[13px] font-medium rounded-xl transition-all duration-200 mt-4 border-t border-white/10 pt-4 ${
                      isActive('/backoffice')
                        ? 'bg-white/10 text-white ring-1 ring-white/10 shadow-sm border-l-[3px] border-secondary-300 pl-[9px]'
                        : 'text-monday-sidebar-text/70 hover:bg-white/[0.07] hover:text-white border-l-[3px] border-transparent pl-[9px]'
                    }`}
                  >
                    <div className="mr-3 flex-shrink-0">{FiIcons.FiLayers({ size: 18 })}</div>
                    Platform
                  </Link>
                )}
              </nav>
            </div>

            {/* User Profile Section */}
            <div className="flex-shrink-0 border-t border-white/10 px-3 py-3 bg-black/10">
              <div className="flex items-center mb-2 px-1 rounded-2xl bg-white/[0.04] p-2 ring-1 ring-white/[0.06]">
                <div className="flex-shrink-0">
                  <div className="h-9 w-9 rounded-2xl bg-primary-500 flex items-center justify-center ring-2 ring-white/20 shadow-primary">
                    <span className="text-white text-sm font-semibold">
                      {userInitials}
                    </span>
                  </div>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-monday-sidebar-text truncate capitalize">
                    {roleName || 'User'}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <Link to="/users" className={footerLinkClass}>
                  <div className="mr-3">{FiIcons.FiUsers({ size: 18 })}</div>
                  Users
                </Link>
              )}
              <button
                type="button"
                onClick={() => setIsChangePasswordOpen(true)}
                className={footerLinkClass}
              >
                <div className="mr-3">{FiIcons.FiLock({ size: 18 })}</div>
                Change password
              </button>
              <button
                type="button"
                onClick={() => setIsNotificationPrefsOpen(true)}
                className={footerLinkClass}
              >
                <div className="mr-3">{FiIcons.FiBell({ size: 18 })}</div>
                Email notifications
              </button>
              <button
                onClick={logout}
                className={footerLinkClass}
              >
                <div className="mr-3">{FiIcons.FiLogOut({ size: 18 })}</div>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {user?.id != null && (
        <ChangePasswordModal
          isOpen={isChangePasswordOpen}
          userId={user.id}
          onClose={() => setIsChangePasswordOpen(false)}
        />
      )}
      <NotificationPreferencesModal
        isOpen={isNotificationPrefsOpen}
        onClose={() => setIsNotificationPrefsOpen(false)}
      />

      {/* ── Main Content Area ───────────────────────────────────────── */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Mobile Header */}
        <div className="md:hidden bg-white/90 backdrop-blur border-b border-monday-border-light shadow-xs">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center">
              <button
                className="h-10 w-10 inline-flex items-center justify-center rounded-xl text-monday-text-muted hover:text-monday-text hover:bg-monday-header focus:outline-none transition-all duration-150"
                onClick={toggleSidebar}
              >
                <span className="sr-only">Open sidebar</span>
                {FiIcons.FiMenu({ size: 22 })}
              </button>
              <div className="ml-3 flex items-center gap-2">
                <svg className="rounded-lg bg-primary-50 p-1" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 3h10v2a5 5 0 0 1-10 0V3Z" fill="#B4233A" fillOpacity="0.18" />
                  <path d="M12 10c3.314 0 6-2.239 6-5V3H6v2c0 2.761 2.686 5 6 5Z" stroke="#B4233A" strokeWidth="1.2" />
                  <path d="M12 10v7m0 0c-2.5 0-4 1-4 2h8c0-1-1.5-2-4-2Z" stroke="#B4233A" strokeWidth="1.2" />
                </svg>
                <h1
                  className="text-sm font-semibold text-monday-text leading-tight truncate"
                  title={activeWineryName}
                >
                  {activeWineryName}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <NotificationBell />
              <div className="h-9 w-9 rounded-2xl bg-primary-500 flex items-center justify-center ring-2 ring-primary-100 shadow-primary">
                <span className="text-white text-xs font-semibold">
                  {userInitials}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center justify-between h-14 px-6 bg-white/90 backdrop-blur border-b border-monday-border-light shadow-xs">
          <h1
            className="text-sm font-semibold text-monday-text truncate tracking-tight"
            title={activeWineryName}
          >
            {activeWineryName}
          </h1>
          <NotificationBell />
        </div>

        {/* Main Content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-monday-bg">
          <div className="py-5 md:py-8">
            <div className="max-w-[1500px] mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
    </NotificationsProvider>
  );
};

export default Layout;
